/*
 *
 * Users
 *
 */

import React from "react";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import { Props, State } from "./types";
import { Helmet } from "react-helmet";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { ContentWrapper, Icon } from "components";
import { Table } from "antd";
import { Modal, Tabs, Tooltip, notification } from "antd";

import Strings from "utils/strings";
import { API, Endpoints } from "utils/api";
import "./styles.scss";
import { distributorSubDomains, localType } from "screens/App/routes";
import xlsx from "json-as-xlsx";
import { Area } from "@ant-design/charts";
import { DateTime } from "luxon";
import { getColumnFilter } from "utils/comp-utils";

export class Users extends React.Component<Props, State> {
	searchTimeout: NodeJS.Timeout | undefined;

	constructor(props: Props) {
		super(props);

		this.state = {
			users: [],
			page: 0,
			pageSize: 100,
			total: 0,
			columnSearch: null,
			globalSearch: "",
			openConfirmResendInviteModal: false,
			tempUser: null,
			showInactive: false,
			clientsChart: null,
			partner: undefined,
			partnerOptions: [],
			partnerSearch: ""
		};
	}

	async componentDidMount() {
		const { dispatch } = this.props;

		dispatch(setTitle(""));
		this.breadcrumb();

		this.getData(undefined);
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	async getData(pause?: boolean) {
		const { page, pageSize, columnSearch, globalSearch, showInactive } = this.state;
		const { dispatch, countries = [] } = this.props;

		setTimeout(
			async () => {
				if (columnSearch !== this.state.columnSearch || globalSearch !== this.state.globalSearch) return;
				dispatch(setLoader(true));

				const body = {
					filters: columnSearch
						? Object.keys(columnSearch).map((elem) =>
								columnSearch[elem] !== ""
									? {
											field: elem,
											query: columnSearch[elem]
									  }
									: {}
						  )
						: [],
					page: page,
					perPage: pageSize,
					search: globalSearch,
					showInactive: showInactive,
					partner: this.state.partner
				};

				try {
					const response = await API.post({
						url: Endpoints.uriUsers("search"),
						data: body
					});

					if (response.ok) {
						const { users = [], total = 0 } = response.data.results;

						for (const user of users) {
							// Set user country
							const userCountryCallingCode = user.phone?.substring(0, 4).replace("+", "");
							const userCountry = countries.find((elem: any) => elem.callingCodes.includes(userCountryCallingCode))
								?.alpha2Code;
							if (userCountry) {
								user.country = userCountry.toLowerCase();
							}

							// Set associatedPartner country
							if (!user.associatedPartner) continue;
							const countryCallingCode = user.associatedPartner?.phone?.substring(0, 4).replace("+", "");
							const country = countries.find((elem: any) => elem.callingCodes.includes(countryCallingCode))?.alpha2Code;
							if (country) {
								user.associatedPartner.country = country.toLowerCase();
							}
						}

						this.setState({ users, total });
					} else {
						notification.error({
							message: Strings.sidebar.users,
							description: response?.data?.message || Strings.serverErrors.wentWrong,
							placement: "bottomRight",
							duration: 5
						});
					}
				} catch (err) {
					notification.error({
						message: Strings.serverErrors.title,
						description: err as string,
						placement: "bottomRight",
						duration: 5
					});
				}

				dispatch(setLoader(false));
			},
			pause ? 1000 : 0
		);
	}

	handleSearch(partner: any) {
		this.setState({ partnerSearch: partner });

		if (partner.length < 3) return;

		if (this.searchTimeout) clearTimeout(this.searchTimeout);

		this.searchTimeout = setTimeout(async () => {
			this.getUsers();
		}, 500);
	}

	async getUsers() {
		const { partnerSearch } = this.state;

		try {
			const body = { search: partnerSearch, page: 0, perPage: 10 };

			const response = await API.post({
				url: Endpoints.uriPartners("search"),
				data: body
			});

			if (response.ok) {
				const { users = [] } = response.data.results || {};
				this.setState({ partnerOptions: users });
			} else {
				notification.error({
					message: Strings.sidebar.users,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				const { partnerOptions } = this.state;

				return {
					locations: [
						{
							text: Strings.sidebar.users,
							icon: "user"
						}
					],
					actions: [
						{
							type: "select",
							text: Strings.fields.partner,
							placeholder: Strings.generic.searchByEmailOrName,
							options: partnerOptions.map((partner: any) => ({
								value: partner._id,
								text: `${partner.name} (${partner.email})`
							})),
							onChange: (partner: any) => {
								this.setState({ partner }, () => this.getData());
							},
							value: this.state.partner,
							showSearch: true,
							onSearch: (value: string) => this.handleSearch(value),
							style: { width: 300, height: 40 },
							allowClear: true
						}
					]
				};
			})
		);
	}

	async deleteUser(id: string) {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.delete({
				url: Endpoints.uriUsers(id)
			});

			if (response.ok) {
				this.getData();
				notification.success({
					message: Strings.sidebar.users,
					description: response?.data?.message || Strings.users.deleted,
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.sidebar.users,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async toggleUser(id: string, active: boolean) {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.patch({
				url: Endpoints.uriUsers(`${id}/status`),
				data: {
					active: !active
				}
			});

			if (response.ok) {
				this.getData();
				notification.success({
					message: Strings.sidebar.users,
					description: response?.data?.message || Strings.users.updated,
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.sidebar.users,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async resendEmail(user: any) {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.patch({
				url: Endpoints.uriUsers(`${user._id}/resend-invite`)
			});

			if (response?.ok) {
				this.setState({ openConfirmResendInviteModal: false, tempUser: null });
				notification.success({
					message: Strings.users.title,
					description: response.data.message,
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	get pagination() {
		return {
			total: this.state.total,
			pageIndex: this.state.page,
			pageSize: this.state.pageSize,
			setPage: (page: number, size: number) => {
				this.setState({ page, pageSize: size }, () => this.getData());
			}
		};
	}

	get filtration() {
		const { globalSearch, columnSearch } = this.state;
		return {
			showGlobalSearch: true,
			showColumnSearch: true,
			defaultValues: { globalSearch, columnSearch },
			onGlobalSearch: (globalSearch: string) => {
				this.setState({ globalSearch, page: 0 }, () => this.getData());
			},
			onColumnSearch: (columnSearch: string) => {
				this.setState({ columnSearch, page: 0 }, () => this.getData());
			}
		};
	}

	getStatus(status: any) {
		if (status) {
			return Strings.staff.confirmed;
		}
		return Strings.staff.pending;
	}

	renderStatus = (elem: any) => {
		if (elem.value === "true")
			return (
				<div className="TableTag --tag-success">
					<span>{this.getStatus(true)}</span>
				</div>
			);

		return (
			<Tooltip title={Strings.authentication.resendInviteEmail}>
				<div
					className="TableTag --tag-warning --tag-clickable"
					onClick={() => this.setState({ openConfirmResendInviteModal: true, tempUser: elem.cell.row.original })}
				>
					<Icon name="refresh" className="resend" />
					<span>{this.getStatus(false)}</span>
				</div>
			</Tooltip>
		);
	};

	exportCustomersToExcel = async () => {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.post({
				url: Endpoints.uriUsers("search"),
				data: {
					limited: false
				}
			});

			if (response.ok) {
				const { users = [] } = response.data.results;

				const content = users.map((s: any) => ({
					name: s.name,
					email: s.email,
					phone: s.phone || "-",
					associatedPartner: s?.associatedPartner?.name,
					active: s.active ? Strings.generic.active : Strings.generic.inactive
				}));

				const data = [
					{
						sheet: "Partners",
						columns: [
							{ label: "Name", value: "name" },
							{ label: "Email", value: "email" },
							{ label: "Phone", value: "phone" },
							{ label: "Associated Partner", value: "associatedPartner" },
							{ label: "Active", value: "active" }
						],
						content: content
					}
				];

				const settings = {
					sheetName: "Customers",
					extraLength: 3,
					writeMode: "writeFile",
					writeOptions: {}
				};

				xlsx(data, settings);
			}
		} catch (error) {
			// notification.error({
			// 	message: Strings.serverErrors.title,
			// 	description: error as string,
			// 	placement: "bottomRight",
			// 	duration: 5
			// });
			console.log("Error exporting users to Excel:", error);
		} finally {
			dispatch(setLoader(false));
		}
	};

	renderUsers() {
		const { users = [] } = this.state;
		const { dispatch, partners } = this.props;

		let add;
		if (!partners) {
			add = {
				tooltip: Strings.users.addUser,
				onClick: () => dispatch(push("/clients/new"))
			};
		} else {
			add = {
				tooltip: Strings.users.addUser,
				onClick: () => dispatch(push("/partner-clients/new"))
			};
		}

		const columns: any[] = [
			{
				title: Strings.fields.country,
				dataIndex: "country",
				key: "country",
				render: (country: any) => {
					return (
						<div className="react-tel-input">
							<div className="selected-flag">
								<span className={`flag ${country}`} />
							</div>
						</div>
					);
				},
				sorter: (a: any, b: any) => a.country?.localeCompare(b.country)
			},
			{
				title: Strings.fields.name,
				dataIndex: "name",
				key: "name",
				render: (name: string) => name,
				sorter: (a: any, b: any) => a.name?.localeCompare(b.name),
				...getColumnFilter("name")
			},
			{
				title: Strings.fields.email,
				dataIndex: "email",
				key: "email",
				render: (email: string) => (email ? <a href={`mailto:${email}`}>{email}</a> : "-"),
				sorter: (a: any, b: any) => a.email?.localeCompare(b.email),
				...getColumnFilter("email")
			},
			{
				title: Strings.fields.phone,
				dataIndex: "phone",
				key: "phone",
				render: (phone: string) => (phone ? <a href={`tel:${phone}`}>{phone}</a> : "-"),
				...getColumnFilter("phone")
			},
			{
				title: Strings.fields.country,
				dataIndex: "associatedPartner",
				key: "associatedPartnerCountry",
				render: (partner: any) => {
					if (!partner) return "-";
					return (
						<div className="react-tel-input">
							<div className="selected-flag">
								<span className={`flag ${partner.country}`} />
							</div>
						</div>
					);
				},
				sorter: (a: any, b: any) => a.associatedPartner?.name?.localeCompare(b.associatedPartner?.name)
			},
			{
				title: Strings.users.associatedPartner,
				dataIndex: "associatedPartner",
				key: "associatedPartner",
				render: (partner: any) => (partner ? partner.name : "-"),
				sorter: (a: any, b: any) => a.associatedPartner?.name?.localeCompare(b.associatedPartner?.name)
			},
			{
				title: Strings.orders.lastOrderDate,
				dataIndex: "lastOrderDate",
				key: "lastOrderDate",
				render: (date: string) => date ? DateTime.fromISO(date).toFormat("dd/MM/yyyy") : "-",
				sorter: (a: any, b: any) => DateTime.fromISO(a.lastOrderDate).toMillis() - DateTime.fromISO(b.lastOrderDate).toMillis()
			},
			{
				title: "CP",
				dataIndex: "cp",
				key: "cp",
				sorter: (a: any, b: any) => a.cp - b.cp,
				render: (cp: number) => cp || 0
			},
			{
				title: "",
				key: "view",
				align: "center",
				width: 60,
				render: (_: any, record: any) => (
					<Icon
						name="eye"
						style={{ cursor: "pointer" }}
						onClick={() => {
							dispatch(push(partners ? `/partner-clients/${record._id}` : `/clients/${record._id}`));
						}}
					/>
				)
			}
		];

		return (
			<ContentWrapper style={{ backgroundColor: "white", padding: "20px" }}>
				<div className="ScreenHeader --multi">
					<div className="ScreenHeaderLeft">
						<Icon name="user" />
						<h2>{Strings.users.title}</h2>
					</div>
					<div className="ScreenHeaderRight">
						<button className="EthicAddButton" onClick={add.onClick} title={add.tooltip}>
							<Icon name="plus" />
						</button>
						<button className="EthicAddButton" onClick={this.exportCustomersToExcel} title={Strings.generic.export}>
							<Icon name="print" />
						</button>
					</div>
				</div>
				<Table
					style={{ marginTop: 20 }}
					scroll={{ x: "max-content" }}
					columns={columns}
					dataSource={users}
					rowKey={(record) => record._id}
				/>
			</ContentWrapper>
		);
	}

	renderConfirmResendInvite = () => {
		const { openConfirmResendInviteModal, tempUser } = this.state;

		return (
			<Modal
				className="resendInvite"
				style={{ textAlign: "center" }}
				visible={openConfirmResendInviteModal}
				cancelText={Strings.generic.close}
				okText={Strings.authentication.resendInviteEmail}
				onOk={() => this.resendEmail(tempUser)}
				onCancel={() => this.setState({ openConfirmResendInviteModal: false })}
				title={null}
				closable={false}
				bodyStyle={{ minHeight: 200 }}
			>
				<Icon name="paper-plane" style={{ fontSize: "50px" }} />
				<div className="title">
					{openConfirmResendInviteModal
						? Strings.formatString(Strings.authentication.confirmResendInviteEmail, tempUser?.name)
						: Strings.authentication.resendInviteEmail}
				</div>
			</Modal>
		);
	};

	render() {
		return (
			<div className="ScreenUsers">
				<Helmet>
					<title>{Strings.sidebar.users}</title>
					<meta name="description" content="Description of Users" />
				</Helmet>

				<ContentWrapper style={{ backgroundColor: "white", padding: "20px" }}>
					<Tabs
						defaultActiveKey="actives"
						className="TabsUsers"
						onChange={(key) => {
							this.setState(
								{
									showInactive: key === "inactives"
								},
								() => {
									this.getData();
								}
							);
						}}
					>
						<Tabs.TabPane tab={Strings.generic.actives} key="actives">
							{this.renderUsers()}
						</Tabs.TabPane>

						<Tabs.TabPane tab={Strings.generic.inactives} key="inactives">
							{this.renderUsers()}
						</Tabs.TabPane>
					</Tabs>
				</ContentWrapper>

				{this.renderConfirmResendInvite()}
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	countries: state.countries
});

export default connect(mapStateToProps)(Users);
